
import PasswordCrypter from '@/utils/password-crypter.js'
import AppException from '@/utils/AppException'
import TranslationService from './services/TranslationService'
import Config from '@/assets/js/config.js'

import AuthService from './services/AuthService'
import AdministratorService from './services/AdministratorService'
import FirebaseService from './services/FirebaseService'
import ContentTemplateService from '@/data/contentTemplate/service.js'
import WebInfoService from '@/data/webInfo/service'
import MessageService from '@/data/message/service'
import SystemLogService from '@/data/systemLog/service'
import PostTagService from '@/data/postTag/service'
import PostService from '@/data/post/service'
import AuthorService from '@/data/author/service.js'
import FBGrabedPostService from '@/data/fbGrabedPost/service.js'
import IGGrabedPostService from '@/data/igGrabedPost/service.js'
import { UserService, UserGiftVoucherService } from '@/data/user/service.js'

import QAService from '@/data/qa/service.js'
import QAGroupService from '@/data/qaGroup/service.js'
import HomeBannerService from '@/data/homeBanner/service.js'
import Order from '@/data/order/service.js'
import Product from '@/data/product/service.js'
import ProductTag from '@/data/productTag/service.js'
import ProductCategory from '@/data/productCategory/service.js'
import ProductCategoryGroup from '@/data/productCategoryGroup/service.js'

// 第二階段
import NewHomeBanner from '@/data/newHomeBanner/service.js'
import Store from '@/data/store/service.js'
import ProductInfo from '@/data/productInfo/service.js'
import ProductInfoTag from '@/data/productInfoTag/service.js'
import OriginInfo from '@/data/originInfo/service.js'
import OriginInfoProduct from '@/data/originInfoProduct/service.js'
import TeamBuying from '@/data/teamBuying/service.js'

// 2022/12改版
import Coupon from '@/data/coupon/service.js'
import Gift from '@/data/gift/service.js'

export default class Services {
  static shared = new Services()

  constructor () {
    let passwordCrypter = new PasswordCrypter(Config.SaltBefore, Config.SaltAfter)
    this.translation = new TranslationService()

    this.auth = new AuthService(passwordCrypter)
    this.administrator = new AdministratorService(passwordCrypter)
    this.firebase = new FirebaseService()
    this.contentTemplate = new ContentTemplateService()
    this.webInfo = new WebInfoService()
    this.message = new MessageService()
    this.systemLog = new SystemLogService()
    this.post = new PostService()
    this.postTag = new PostTagService()
    this.author = new AuthorService()
    this.fbGrabedPost = new FBGrabedPostService()
    this.igGrabedPost = new IGGrabedPostService()
    this.user = new UserService(passwordCrypter)
    this.userGiftVoucher = new UserGiftVoucherService()

    this.qa = new QAService()
    this.qaGroup = new QAGroupService()
    this.homeBanner = new HomeBannerService()
    this.order = new Order()
    this.product = new Product()
    this.productTag = new ProductTag()
    this.productCategory = new ProductCategory()
    this.productCategoryGroup = new ProductCategoryGroup()

    // 第二階段
    this.newHomeBanner = new NewHomeBanner()
    this.store = new Store()
    this.productInfo = new ProductInfo()
    this.productInfoTag = new ProductInfoTag()
    this.originInfo = new OriginInfo()
    this.originInfoProduct = new OriginInfoProduct()
    this.teamBuying = new TeamBuying()

    // 2022/12改版
    this.coupon = new Coupon()
    this.gift = new Gift()
  }

  fetchMe (vm) {
    return Promise.all([
      this.administrator.getMe(vm).then(o => { vm.isLoading = true; return o })
    ]).then(results => {
      let admin = results[0].data.result

      // vuex
      vm.$store.state.admin = admin
    }).finally(() => {
      vm.isLoading = false
    })
  }

  fetchDatas (vm) {
    return Promise.all([
      this.contentTemplate.getAll(vm).then(o => { vm.isLoading = true; return o }),
      this.webInfo.getAll(vm).then(o => { vm.isLoading = true; return o }),
      this.message.getAll(vm).then(o => { vm.isLoading = true; return o })
    ]).then(results => {
      let contentTemplateList = results[0].data.result
      let webInfoList = results[1].data.result
      let messagelist = results[2].data.result

      // vuex
      vm.$store.state.contentTemplate.list = contentTemplateList
      vm.$store.state.webInfo.list = webInfoList
      vm.$store.state.message.list = messagelist
    }).finally(() => {
      vm.isLoading = false
    })
  }

  fetchPostDatas (vm) {
    if (vm.$store.state.post.isFetching) throw new AppException('正在讀取中')
    vm.$store.state.post.isFetching = true
    return Promise.all([
      this.post.getAll(vm).then(o => { vm.isLoading = true; return o }),
      this.postTag.getAll(vm).then(o => { vm.isLoading = true; return o }),
      this.author.getAll(vm).then(o => { vm.isLoading = true; return o })
    ]).then(results => {
      let postList = results[0].data.result
      let postTagList = results[1].data.result
      let authorList = results[2].data.result

      // 計算標籤使用程度
      this.postTag.calcuateUsedTimes(postTagList, postList)

      // vuex
      vm.$store.state.post.list = postList
      vm.$store.state.postTag.list = postTagList
      vm.$store.state.author.list = authorList
      vm.$store.state.post.commonlyUsedTagList = this.postTag.findCommonlyUsedList(postTagList)

      vm.$store.state.post.isFetched = true
    }).finally(() => {
      vm.isLoading = false
      vm.$store.state.post.isFetching = false
    })
  }

  fetchProductDatas (vm) {
    if (vm.$store.state.product.isFetching) throw new AppException('正在讀取中')
    vm.$store.state.product.isFetching = true
    return Promise.all([
      this.product.getAll(vm).then(o => { vm.isLoading = true; return o }),
      this.productTag.getAll(vm).then(o => { vm.isLoading = true; return o }),
      this.productCategory.getAll(vm).then(o => { vm.isLoading = true; return o }),
      this.productCategoryGroup.getAll(vm).then(o => { vm.isLoading = true; return o }),
      this.author.getAll(vm).then(o => { vm.isLoading = true; return o })
    ]).then(results => {
      let productList = results[0].data.result
      let productTagList = results[1].data.result
      let productCategoryList = results[2].data.result
      let productCategoryGroupList = results[3].data.result

      // vuex
      vm.$store.state.product.list = productList.sort((o1, o2) => o1.SortOrder - o2.SortOrder)
      vm.$store.state.productTag.list = productTagList
      vm.$store.state.productCategory.list = productCategoryList
      vm.$store.state.productCategoryGroup.list = productCategoryGroupList

      vm.$store.state.product.isFetched = true
    }).finally(() => {
      vm.isLoading = false
      vm.$store.state.product.isFetching = false
    })
  }

  fetchProductInfoDatas (vm) {
    if (vm.$store.state.productInfo.isFetching) throw new AppException('正在讀取中')
    vm.$store.state.productInfo.isFetching = true
    return Promise.all([
      this.productInfo.getAll(vm).then(o => { vm.isLoading = true; return o }),
      this.productInfoTag.getAll(vm).then(o => { vm.isLoading = true; return o })
    ]).then(results => {
      let productInfoList = results[0].data.result
      let productInfoTagList = results[1].data.result

      // vuex
      vm.$store.state.productInfo.list = productInfoList
      vm.$store.state.productInfoTag.list = productInfoTagList

      vm.$store.state.productInfo.isFetched = true
    }).finally(() => {
      vm.isLoading = false
      vm.$store.state.productInfo.isFetching = false
    })
  }

  fetchOriginInfoDatas (vm) {
    if (vm.$store.state.originInfo.isFetching) throw new AppException('正在讀取中')
    vm.$store.state.originInfo.isFetching = true
    return Promise.all([
      this.originInfo.getAll(vm).then(o => { vm.isLoading = true; return o }),
      this.originInfoProduct.getAll(vm).then(o => { vm.isLoading = true; return o })
    ]).then(results => {
      let originInfoList = results[0].data.result
      let originInfoProductList = results[1].data.result

      // vuex
      vm.$store.state.originInfo.list = originInfoList
      vm.$store.state.originInfoProduct.list = originInfoProductList

      vm.$store.state.originInfo.isFetched = true
    }).finally(() => {
      vm.isLoading = false
      vm.$store.state.originInfo.isFetching = false
    })
  }

  fetchFBGrabedPostDatas (vm) {
    if (vm.$store.state.fbGrabedPost.isFetching) throw new AppException('正在讀取中')
    vm.$store.state.fbGrabedPost.isFetching = true
    return Promise.all([
      this.fbGrabedPost.getAll(vm).then(o => { vm.isLoading = true; return o })
    ]).then(results => {
      let fbGrabedPostList = results[0].data.result

      // vuex
      vm.$store.state.fbGrabedPost.list = fbGrabedPostList

      vm.$store.state.fbGrabedPost.isFetched = true
    }).finally(() => {
      vm.isLoading = false
      vm.$store.state.fbGrabedPost.isFetching = false
    })
  }

  fetchIGGrabedPostDatas (vm) {
    if (vm.$store.state.igGrabedPost.isFetching) throw new AppException('正在讀取中')
    vm.$store.state.igGrabedPost.isFetching = true
    return Promise.all([
      this.igGrabedPost.getAll(vm).then(o => { vm.isLoading = true; return o })
    ]).then(results => {
      let igGrabedPostList = results[0].data.result

      // vuex
      vm.$store.state.igGrabedPost.list = igGrabedPostList

      vm.$store.state.igGrabedPost.isFetched = true
    }).finally(() => {
      vm.isLoading = false
      vm.$store.state.igGrabedPost.isFetching = false
    })
  }

  // alertResultError (vm, res) {
  //   let resultError = (res && res.data && res.data.error) || '未知的錯誤'
  //   vm.$alert(resultError, '錯誤', {
  //     confirmButtonText: '確認',
  //     type: 'error'
  //   })
  // }

  // alertError (vm, err) {
  //   console.error(err)
  //   var msg = err
  //   if (err.response && err.response.data && err.response.data.error) msg = err.response.data.error
  //   else if (err.response) msg = (err.response.status || '') + ' ' + (err.response.statusText || '沒有錯誤訊息')
  //   else msg = '未知的錯誤'

  //   vm.$alert(msg, '錯誤', {
  //     confirmButtonText: '確認',
  //     type: 'error'
  //   })
  // }
}
